import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { AdapterOptions } from '../../../../../adapters/types'
import { RocFormData } from '../../../../types/RocFormData'
import { RocFormField } from '../../../../types/RocFormField'
import { RocAssessmentQuestionnaireItem } from '../../../../types/questionnaire'
import { getAnswerValuesFromItems } from '../../../../../helpers'
import { BaseSubsectionAdapter } from '../../../../../adapters/BaseSubsectionAdapter'
import { FlatAnswerMutation } from '../../../../../adapters/types'

/**
 * Respiratory subsection adapter
 * Handles respiratory assessment, cough, oxygen, and lung sounds (5 fields)
 */
export class RespiratoryAdapter extends BaseSubsectionAdapter<RocFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<RocFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Respiratory assessment fields
    const respiratoryStatus = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RESPIRATORY_RESPIRATORY_WNL
    )?.at(0)

    const cough = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RESPIRATORY_COUGH
    )?.at(0)

    const oxygen = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RESPIRATORY_OXYGEN
    )?.at(0)

    const lungSounds = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RESPIRATORY_LUNG_SOUNDS
    )

    const systemSummary = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RESPIRATORY_SYSTEM_SUMMARY
    )?.at(0)

    return {
      [RocFormField.RESPIRATORY_STATUS]: respiratoryStatus,
      [RocFormField.RESPIRATORY_COUGH]: cough,
      [RocFormField.RESPIRATORY_OXYGEN]: oxygen,
      [RocFormField.RESPIRATORY_LUNG_SOUNDS]: lungSounds,
      [RocFormField.RESPIRATORY_SYSTEM_SUMMARY]: systemSummary
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []

    if (this.formDataFieldExists(RocFormField.RESPIRATORY_STATUS)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RESPIRATORY_RESPIRATORY_WNL,
        value: this.getFormDataField(RocFormField.RESPIRATORY_STATUS)
      })
    }

    if (this.formDataFieldExists(RocFormField.RESPIRATORY_COUGH)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RESPIRATORY_COUGH,
        value: this.getFormDataField(RocFormField.RESPIRATORY_COUGH)
      })
    }

    if (this.formDataFieldExists(RocFormField.RESPIRATORY_OXYGEN)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RESPIRATORY_OXYGEN,
        value: this.getFormDataField(RocFormField.RESPIRATORY_OXYGEN)
      })
    }

    if (this.formDataFieldExists(RocFormField.RESPIRATORY_LUNG_SOUNDS)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RESPIRATORY_LUNG_SOUNDS,
        value: this.getFormDataField(RocFormField.RESPIRATORY_LUNG_SOUNDS)
      })
    }

    if (this.formDataFieldExists(RocFormField.RESPIRATORY_SYSTEM_SUMMARY)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RESPIRATORY_SYSTEM_SUMMARY,
        value: this.getFormDataField(RocFormField.RESPIRATORY_SYSTEM_SUMMARY)
      })
    }

    return mutations
  }
}
